<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div
			class="flex items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg
						class="w-5 h-5 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
					</svg>
				</div>
				<div>
					<h1
						class="font-display text-xl md:text-2xl font-extrabold tracking-tight text-dark-700 dark:text-white">
						User Management
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Create a new user account
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<!-- ── Toast messages ── -->
		<transition name="fade">
			<div
				v-if="successMessage"
				class="flex items-center gap-3 p-4 mb-4 rounded-xl text-[13px] font-medium bg-primary-50 dark:bg-primary-400/15 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-400/30">
				<svg
					class="w-4 h-4 flex-shrink-0"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2.5"
						d="M5 13l4 4L19 7" />
				</svg>
				{{ successMessage }}
			</div>
		</transition>

		<transition name="fade">
			<div
				v-if="errorMessage"
				class="flex items-center gap-3 p-4 mb-4 rounded-xl text-[13px] font-medium bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800/50">
				<svg
					class="w-4 h-4 flex-shrink-0"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>
				{{ errorMessage }}
			</div>
		</transition>

		<transition name="fade">
			<div
				v-if="infoMessage"
				class="flex items-center gap-3 p-4 mb-4 rounded-xl text-[13px] font-medium bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50">
				<svg
					class="w-4 h-4 flex-shrink-0"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>
				{{ infoMessage }}
			</div>
		</transition>

		<transition name="fade">
			<div
				v-if="warningMessage"
				class="flex items-center gap-3 p-4 mb-4 rounded-xl text-[13px] font-medium bg-accent-50 dark:bg-accent-400/10 text-accent-700 dark:text-accent-400 border border-accent-200 dark:border-accent-400/30">
				<svg
					class="w-4 h-4 flex-shrink-0"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
				</svg>
				{{ warningMessage }}
			</div>
		</transition>

		<!-- ── Form ── -->
		<form @submit.prevent="createUser" class="space-y-4">
			<!-- ══ CARD — User Info ══ -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
				<p
					class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
					Account Details
				</p>

				<div class="flex flex-col gap-5">
					<!-- Full Name -->
					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
							</svg>
							Full Name
						</label>
						<input
							type="text"
							v-model="user.name"
							required
							class="form-input"
							:class="{
								'border-red-400': errors.name,
								'border-primary-400': !errors.name && user.name,
							}" />
						<ErrorMessage :message="errors.name" v-if="errors.name" />
					</div>

					<!-- Email -->
					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
							</svg>
							Email Address
						</label>
						<input
							type="text"
							v-model="user.email"
							required
							class="form-input"
							:class="{
								'border-red-400': errors.email,
								'border-primary-400': !errors.email && user.email,
							}" />
						<ErrorMessage :message="errors.email" v-if="errors.email" />
					</div>

					<!-- Phone -->
					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
							</svg>
							Phone Number
						</label>
						<input
							type="text"
							v-model="user.phone"
							required
							class="form-input"
							:class="{
								'border-red-400': errors.phone,
								'border-primary-400': !errors.phone && user.phone,
							}" />
						<ErrorMessage :message="errors.phone" v-if="errors.phone" />
					</div>
				</div>
			</div>

			<!-- ── Divider ── -->
			<div class="form-divider" />

			<!-- ── Submit ── -->
			<button
				type="submit"
				:disabled="isSubmitting || !isValidForm"
				class="w-full font-display font-bold text-[14px] py-3.5 rounded-xl bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card hover:shadow-teal disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200">
				<span v-if="!isLoading" class="flex items-center justify-center gap-2">
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2.5"
							d="M5 13l4 4L19 7" />
					</svg>
					Save User
				</span>
				<span v-else class="flex items-center justify-center gap-2">
					<svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4" />
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
					</svg>
					Processing...
				</span>
			</button>
		</form>
	</div>
</template>

<script>
import { reactive, ref, computed } from "vue";
import { apiDataService } from "@/services/apiDataService";
import { showToast } from "@/constants/toast";
import { getErrorMessage } from "@/utils/errorHandler";
import { useRouter } from "vue-router";
import "vue-toast-notification/dist/theme-sugar.css";
import { useStore } from "vuex";
import ErrorMessage from "@/components/messages/ErrorMessage.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";

export default {
	components: { ErrorMessage, Breadcrumbs },

	setup() {
		const router = useRouter();
		const isSubmitting = ref(false);
		const isValidForm = ref(true);
		const successMessage = ref("");
		const errorMessage = ref("");
		const infoMessage = ref("");
		const warningMessage = ref("");
		const messageTimeout = ref(null);

		const user = reactive({ name: "", email: "", phone: "", organization: "" });
		const errors = reactive({ name: null, email: null, phone: null });

		const setMessage = (type, text, duration = 5000) => {
			clearTimeout(messageTimeout.value);
			successMessage.value = "";
			errorMessage.value = "";
			infoMessage.value = "";
			warningMessage.value = "";
			if (type === "success") successMessage.value = text;
			else if (type === "info") infoMessage.value = text;
			else if (type === "warning") warningMessage.value = text;
			else errorMessage.value = text;
			messageTimeout.value = setTimeout(() => {
				successMessage.value = "";
				errorMessage.value = "";
				infoMessage.value = "";
				warningMessage.value = "";
			}, duration);
		};

		const store = useStore();
		const isLoading = computed(() => store.state.category.loading);
		const userInfo = computed(() => store.state.auth.user || {});
		user.organization = userInfo.value.organization_id;

		const createUser = async () => {
			isSubmitting.value = true;
			store.commit("category/SET_LOADING", true);
			try {
				const response = await apiDataService.createUser(
					user,
					userInfo.value.organization_id,
				);
				showToast(
					"success",
					`${response?.Name || "New"} user created successfully!`,
				);
				router.push("/admin/user/all");
			} catch (error) {
				const message = getErrorMessage(error);
				showToast("error", message);
			} finally {
				isSubmitting.value = false;
				store.commit("category/SET_LOADING", false);
			}
		};

		return {
			isSubmitting,
			isLoading,
			isValidForm,
			user,
			errors,
			createUser,
			successMessage,
			errorMessage,
			infoMessage,
			warningMessage,
			setMessage,
		};
	},
};
</script>

<style scoped>
.form-label {
	@apply flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest mb-2
         text-muted dark:text-muted-light;
}
.form-input {
	@apply w-full px-3.5 py-2.5 rounded-xl text-[13px] font-medium
         bg-primary-50/60 dark:bg-dark-600
         border border-primary-100 dark:border-dark-500
         text-dark-700 dark:text-white
         placeholder-muted dark:placeholder-muted-light
         outline-none transition-all duration-200
         focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20
         dark:focus:border-primary-400;
}
.form-divider {
	@apply flex items-center gap-3 my-1;
}
.form-divider::before,
.form-divider::after {
	content: "";
	@apply flex-1 border-t border-dashed border-primary-100 dark:border-dark-600;
}
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
